import type { en } from './en'

/**
 * Farsi (فارسی). Typed against `en`, so a missing key fails the build.
 *
 * Notes on choices:
 * - Informal-but-respectful register, matching the warmth of the English copy.
 * - "Audition" → «آزمون اجرا» rather than the loanword «ادیشن», which is common
 *   in speech but reads as slang in a document you might show a university.
 * - ZWNJ (نیم‌فاصله, U+200C) is used in compounds — «آب‌وهوا», «شخصی‌سازی» — so
 *   they render as one word rather than breaking across a space.
 */
export const fa: Record<keyof typeof en, string> = {
  /* ------------------------------------------------------------ app shell */
  'app.tagline': 'الا · مرکز شخصی',
  'nav.today': 'امروز',
  'nav.calendar': 'تقویم',
  'nav.study': 'مطالعه',
  'nav.journey': 'مسیر',
  'nav.library': 'کتابخانه',
  'nav.more': 'بیشتر',

  /* -------------------------------------------------------------- common */
  'common.save': 'ذخیره',
  'common.cancel': 'انصراف',
  'common.edit': 'ویرایش',
  'common.delete': 'حذف',
  'common.close': 'بستن',
  'common.add': 'افزودن',
  'common.done': 'تمام',
  'common.none': 'هیچ‌کدام',
  'common.of': 'از',
  'common.arrow': '←',

  /* ---------------------------------------------------------------- time */
  'time.morning': 'صبح بخیر',
  'time.afternoon': 'عصر بخیر',
  'time.evening': 'شب بخیر',
  'time.today': 'امروز',
  'time.tomorrow': 'فردا',
  'time.yesterday': 'دیروز',
  'time.inDays': '{n} روز دیگر',
  'time.daysAgo': '{n} روز پیش',
  'time.overdue': 'گذشته',
  'time.dueToday': 'مهلت امروز',
  'time.days': '{n} روز',
  'time.weeks': '{n} هفته',
  'time.hours': '{n} ساعت',
  'time.minutes': '{n} دقیقه',
  'time.hoursMinutes': '{h} ساعت و {m} دقیقه',
  'time.hoursShort': '{n} ساعت',
  'time.minutesShort': '{n} دقیقه',

  /* --------------------------------------------------------------- today */
  'today.comma': '،',
  'today.customise': 'شخصی‌سازی',
  'today.openTasks': '{n} کار باز',
  'today.openTask': '{n} کار باز',
  'today.nothingOpen': 'چیزی باز نیست',
  'today.streak': '{n} روز پیاپی',
  'today.toInstitution': '{time} تا {name}',

  'tile.countdown': 'مهلت',
  'tile.nextUp': 'بعدی',
  'tile.tasks': 'کارها',
  'tile.studyWeek': 'هفتهٔ مطالعه',
  'tile.audition': 'آزمون اجرا',
  'tile.quickNote': 'یادداشت سریع',
  'tile.weather': 'آب‌وهوا',
  'tile.player': 'پخش‌کننده',

  'tile.moveEarlier': 'انتقال به عقب',
  'tile.moveLater': 'انتقال به جلو',
  'tile.resize': 'تغییر اندازه',
  'tile.hide': 'پنهان کردن {name}',

  'tile.nextUpTitle': 'بعدی',
  'tile.nothingScheduled': 'برنامه‌ای نیست. از آن لذت ببر',
  'tile.tasksTitle': 'کارها',
  'tile.all': 'همه',
  'tile.allClear': 'همه‌چیز تمام است.',
  'tile.thisWeek': 'این هفته',
  'tile.ofTarget': 'از هدف {x}',
  'tile.auditionTitle': 'آزمون اجرا',
  'tile.piecesInProgress': 'قطعه در دست کار',
  'tile.pieceInProgress': 'قطعه در دست کار',
  'tile.quickNoteTitle': 'یادداشت سریع',
  'tile.quickNotePlaceholder': 'یک خط، پیش از آنکه از یاد برود…',
  'tile.pinIt': 'سنجاق کن',
  'tile.notes': 'یادداشت‌ها',
  'tile.nowPlaying': 'در حال پخش',
  'tile.nothingYet': 'هنوز چیزی نیست',
  'tile.pickSomething': 'از کتابخانه چیزی انتخاب کن',
  'tile.library': 'کتابخانه',
  'tile.journeyTitle': 'مسیر',
  'tile.addFirstApplication': 'اولین درخواستت را اضافه کن',
  'tile.requirementsDone': '{done} از {total} الزام انجام شد',

  'weather.checking': 'در حال نگاه به آسمان…',
  'weather.unavailable': 'آب‌وهوا در دسترس نیست',
  'weather.clear': 'آسمان صاف',
  'weather.partlyCloudy': 'نیمه‌ابری',
  'weather.overcast': 'ابری',
  'weather.rain': 'باران',
  'weather.snow': 'برف',
  'weather.showers': 'رگبار',
  'weather.storms': 'طوفان',

  'player.play': 'پخش',
  'player.pause': 'مکث',

  /* ------------------------------------------------------------ calendar */
  'calendar.title': 'تقویم',
  'calendar.event': 'رویداد',
  'calendar.month': 'ماه',
  'calendar.agenda': 'فهرست',
  'calendar.everythingAhead': 'هرچه در پیش است',
  'calendar.prevMonth': 'ماه قبل',
  'calendar.nextMonth': 'ماه بعد',
  'calendar.dayLabel': '{date}، {n} رویداد',
  'calendar.nothingOn': 'چیزی نیست',
  'calendar.nothingOnBody': 'برای افزودن، «رویداد» را بزن.',
  'calendar.clearHorizon': 'افق خالی',
  'calendar.clearHorizonBody': 'از امروز به بعد چیزی در برنامه نیست.',
  'calendar.newEvent': 'رویداد جدید',
  'calendar.editEvent': 'ویرایش رویداد',
  'calendar.deleteEvent': 'حذف رویداد',
  'calendar.markDone': 'انجام شد',
  'calendar.markNotDone': 'انجام نشده',
  'calendar.fieldTitle': 'عنوان',
  'calendar.fieldDate': 'تاریخ',
  'calendar.fieldStart': 'شروع',
  'calendar.fieldEnd': 'پایان',
  'calendar.fieldKind': 'نوع',
  'calendar.fieldNotes': 'یادداشت',
  'calendar.titlePlaceholder': 'کلاس آهنگسازی',
  'calendar.notesPlaceholder': 'چیزی که باید یادت بماند…',

  'kind.class': 'کلاس',
  'kind.study': 'مطالعه',
  'kind.deadline': 'مهلت',
  'kind.audition': 'آزمون اجرا',
  'kind.personal': 'شخصی',

  /* --------------------------------------------------------------- study */
  'study.title': 'مطالعه',
  'study.oneSession': 'هر بار یک جلسه',
  'study.streakKeep': '{n} روز پیاپی — ادامه بده',
  'study.tabFocus': 'تمرکز',
  'study.tabTasks': 'کارها',
  'study.tabSubjects': 'درس‌ها',
  'study.phaseFocus': 'تمرکز',
  'study.phaseBreak': 'استراحت',
  'study.start': 'شروع',
  'study.pause': 'مکث',
  'study.resume': 'ادامه',
  'study.reset': 'بازنشانی',
  'study.logAndStop': 'ثبت و توقف',
  'study.thisWeek': 'این هفته',
  'study.weekSummary': '{done} از {target}',
  'study.addTaskPlaceholder': 'افزودن کار…',
  'study.newTaskLabel': 'کار جدید',
  'study.addWithDetails': 'افزودن با جزئیات',
  'study.openCount': 'باز · {n}',
  'study.nothingOpen': 'چیزی باز نیست',
  'study.nothingOpenBody': 'همهٔ کارهای فهرستت تمام شده.',
  'study.doneSection': 'انجام‌شده',
  'study.newTask': 'کار جدید',
  'study.fieldTask': 'کار',
  'study.taskPlaceholder': 'تمام کردن تنظیم قطعه…',
  'study.fieldSubject': 'درس',
  'study.fieldDue': 'سررسید',
  'study.important': 'مهم',
  'study.addTask': 'افزودن کار',
  'study.addSubject': 'افزودن درس',
  'study.subjectName': 'نام درس',
  'study.subjectPlaceholder': 'آهنگسازی',
  'study.weeklyTarget': 'هدف هفتگی',
  'study.targetPerWeek': 'هدف {x} در هفته',
  'study.targetHours': 'هدف هفتگی {name} به ساعت',
  'study.setColour': 'رنگ {name} روی {hue}',
  'study.deleteNamed': 'حذف {name}',

  /* ------------------------------------------------------------- journey */
  'journey.title': 'مسیر',
  'journey.subtitle': 'هر درخواست، و آنچه هنوز لازم دارد',
  'journey.whatsNext': 'قدم بعدی',
  'journey.noApplications': 'هنوز درخواستی نیست',
  'journey.noApplicationsBody':
    'اولین دانشگاهی را که هدف گرفته‌ای اضافه کن و الزاماتش را همین‌جا دنبال کن.',
  'journey.deadline': 'مهلت {date}',
  'journey.addRequirement': 'افزودن الزام…',
  'journey.newRequirement': 'الزام جدید',
  'journey.removeRequirement': 'حذف {name}',
  'journey.newApplication': 'درخواست جدید',
  'journey.editApplication': 'ویرایش درخواست',
  'journey.deleteApplication': 'حذف درخواست',
  'journey.fieldInstitution': 'دانشگاه',
  'journey.fieldCourse': 'رشته',
  'journey.fieldDeadline': 'مهلت',
  'journey.fieldStatus': 'وضعیت',
  'journey.fieldNotes': 'یادداشت',
  'journey.institutionPlaceholder': 'دانشگاه آدلاید — کنسرواتوار الدر',
  'journey.coursePlaceholder': 'کارشناسی موسیقی (اجرای خلاق)',
  'journey.notesPlaceholder': 'آنچه دربارهٔ این یکی مهم است…',

  'status.researching': 'در حال بررسی',
  'status.preparing': 'در حال آماده‌سازی',
  'status.submitted': 'ارسال‌شده',
  'status.interview': 'مصاحبه',
  'status.offer': 'پذیرش',
  'status.declined': 'رد شده',

  /* ------------------------------------------------------------- library */
  'library.title': 'کتابخانه',
  'library.subtitle': 'رپرتوار، صفحه‌ها و فکرهای پراکنده',
  'library.tabMusic': 'موسیقی',
  'library.tabWiki': 'ویکی',
  'library.tabNotes': 'یادداشت‌ها',
  'library.allTracks': 'همهٔ قطعه‌ها',
  'library.auditionOnly': 'فقط آزمون اجرا',
  'library.upload': 'بارگذاری',
  'library.uploadLabel': 'بارگذاری یک فایل صوتی',
  'library.uploadHint':
    'صدای بارگذاری‌شده فقط در همین نشست پخش می‌شود — خود فایل روی دستگاه تو می‌ماند و در حافظهٔ برنامه کپی نمی‌شود.',
  'library.nothingHere': 'اینجا چیزی نیست',
  'library.nothingHereBody': 'یک ضبط بارگذاری کن تا ساختن آزمون اجرا شروع شود.',
  'library.auditionBadge': 'آزمون اجرا',
  'library.myUpload': 'بارگذاری من',
  'library.playNamed': 'پخش {name}',
  'library.pauseNamed': 'مکث {name}',
  'library.removeNamed': 'حذف {name}',
  'library.noPages': 'هنوز صفحه‌ای نیست',
  'library.noPagesBody': 'صفحه‌های ویکی مارک‌داون هستند — برای پژوهش و یادداشت خوب‌اند.',
  'library.newPage': 'صفحهٔ جدید',
  'library.newPageShort': '+ جدید',
  'library.untitledPage': 'صفحهٔ بی‌نام',
  'library.livePreview': 'پیش‌نمایش زنده',
  'library.editedRel': 'ویرایش {when}',
  'library.markdownSource': 'متن مارک‌داون',
  'library.quickNote': 'یادداشت سریع',
  'library.quickNotePlaceholder': 'به هر زبانی که خواستی بنویس…',
  'library.pinNote': 'سنجاق یادداشت',
  'library.nothingPinned': 'چیزی سنجاق نشده',
  'library.nothingPinnedBody': 'یک یادداشت سریع را سنجاق کن تا اینجا بیاید.',
  'library.deleteNote': 'حذف یادداشت',

  /* ------------------------------------------------------------ settings */
  'settings.title': 'تنظیمات',
  'settings.subtitle': 'همه‌چیز روی همین دستگاه ذخیره می‌شود',
  'settings.blockedTitle': 'تغییرها ذخیره نمی‌شوند',
  'settings.blockedBody':
    'حافظهٔ مرورگر پر است یا غیرفعال شده (حالت ناشناس همین کار را می‌کند). برنامه کار می‌کند، اما هرچه اضافه کنی با بستن تب از بین می‌رود.',
  'settings.you': 'دربارهٔ تو',
  'settings.name': 'نام',
  'settings.city': 'شهر (برای آب‌وهوا)',
  'settings.appearance': 'ظاهر',
  'settings.language': 'زبان',
  'settings.languageHint': 'زبان رابط را عوض می‌کند. هرچه خودت نوشته‌ای دست‌نخورده می‌ماند.',
  'settings.theme': 'پوسته',
  'settings.themeHint': '«سیستم» از تنظیم روشن/تیرهٔ دستگاهت پیروی می‌کند.',
  'settings.themeLight': 'روشن',
  'settings.themeDark': 'تیره',
  'settings.themeSystem': 'سیستم',
  'settings.accent': 'رنگ تأکید',
  'settings.accentHint': 'برای تأکیدها، حلقهٔ تمرکز و نوار پیشرفت استفاده می‌شود.',
  'settings.plan': 'برنامه',
  'settings.intake': 'ترم هدف',
  'settings.intakeHint':
    'زمان‌بندی اولیه را می‌سازد. تا وقتی چیزی را دست نزده‌ای، تغییر آن تاریخ‌های برنامه را جابه‌جا می‌کند — پس از آن، تاریخ‌های خودت حفظ می‌شوند.',
  'settings.focusTimer': 'زمان‌سنج تمرکز',
  'settings.focusBlock': 'بازهٔ تمرکز',
  'settings.break': 'استراحت',
  'settings.timerLength': 'مدت {label} به دقیقه',
  'settings.yourData': 'داده‌های تو',
  'settings.dataHint':
    'هیچ‌چیز از این دستگاه بیرون نمی‌رود — نه حسابی هست نه سروری. یعنی پاک کردن دادهٔ مرورگر، برنامه را هم پاک می‌کند؛ پس گاهی پشتیبان بگیر. مصرف فعلی: {kb} کیلوبایت.',
  'settings.export': 'خروجی پشتیبان',
  'settings.import': 'بازیابی پشتیبان',
  'settings.chooseBackup': 'انتخاب فایل پشتیبان',
  'settings.resetTitle': 'بازنشانی همه‌چیز',
  'settings.resetHint':
    'همهٔ رویدادها، کارها، درخواست‌ها و یادداشت‌هایت را پاک می‌کند و محتوای اولیه را برمی‌گرداند. برگشت‌پذیر نیست.',
  'settings.resetConfirm': 'بله، پاک کن',
  'settings.reset': 'بازنشانی',

  'intake.aug2027': 'اوت ۲۰۲۷ — ترم دوم',
  'intake.feb2028': 'فوریهٔ ۲۰۲۸',

  'toast.backupDownloaded': 'پشتیبان دانلود شد',
  'toast.backupRestored': 'پشتیبان بازیابی شد',
  'toast.importFailed': 'بازیابی نشد — {error}',
  'toast.everythingReset': 'همه‌چیز بازنشانی شد',
  'toast.replanned': 'برنامه برای {intake} زمان‌بندی شد',
  'toast.keptYourDates': 'زبان رابط عوض شد. محتوای خودت دست‌نخورده ماند.',
}
